import { useState, useMemo } from 'react';
import { useBudgetListQuery } from '../hooks/useBudgetQueries';
import type { BudgetWithFiltersItemDto } from '../api';
import type { BudgetListParams } from '../api';
import { SearchIcon } from '@/shared/ui/Icons';
import { LoadingSpinner } from '@/shared/components';

interface BudgetListPanelProps {
  selectionTitle: string | null;
  listParams: BudgetListParams;
  onClearSelection: () => void;
}

export function BudgetListPanel({
  selectionTitle,
  listParams,
  onClearSelection,
}: BudgetListPanelProps) {
  const [search, setSearch] = useState('');
  const { data, isLoading, error: listError } = useBudgetListQuery(listParams);
  const budgets = data?.items ?? [];

  const filteredBudgets = useMemo(() => {
    if (!search.trim()) return budgets;
    const q = search.trim().toLowerCase();
    const statusText = (b: BudgetWithFiltersItemDto) =>
      b.budgetStatus === 0 ? 'compliant' : 'non-compliant';
    return budgets.filter(
      (b) =>
        (b.school?.schoolName?.toLowerCase().includes(q) ?? false) ||
        (b.school?.educationalCenterNumber?.toString().toLowerCase().includes(q) ?? false) ||
        statusText(b).includes(q) ||
        (b.budgetStatus === 0 && q === '0') ||
        (b.budgetStatus === 2 && q === '2')
    );
  }, [budgets, search]);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-soft">
      <div className="border-b border-slate-200 px-6 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Budgets</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              {selectionTitle != null ? (
                <>
                  Showing:{' '}
                  <span className="font-medium text-slate-700">
                    {selectionTitle}
                  </span>
                </>
              ) : (
                'Click a chart segment, point, or bar to filter the list below'
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by number, school, status..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="min-w-[220px] w-full rounded-lg border border-slate-200 bg-slate-50/50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            {selectionTitle != null && (
              <button
                type="button"
                onClick={onClearSelection}
                className="shrink-0 text-sm text-indigo-600 hover:text-indigo-700"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="h-[600px] min-h-[600px] overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : listError ? (
          <p className="py-12 text-center text-rose-600">
            Failed to load: {String(listError)}
          </p>
        ) : filteredBudgets.length === 0 ? (
          <p className="py-12 text-center text-slate-500">
            {budgets.length === 0
              ? 'No budgets found'
              : 'No matches for your search'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-[1] bg-slate-50">
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="px-6 py-3 font-medium">Number</th>
                <th className="px-6 py-3 font-medium">School</th>
                <th className="px-6 py-3 font-medium">Year</th>
                <th className="px-6 py-3 font-medium">Submitted</th>
                <th className="px-6 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBudgets.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50"
                >
                  <td className="px-6 py-3 font-mono text-slate-600">
                    {b.school?.educationalCenterNumber || '—'}
                  </td>
                  <td className="px-6 py-3 text-slate-900">{b.school?.schoolName ?? '—'}</td>
                  <td className="px-6 py-3 text-slate-600">{b.year}</td>
                  <td className="px-6 py-3 text-slate-600">
                    {b.submitedDate
                      ? new Date(b.submitedDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={
                        b.budgetStatus === 0
                          ? 'text-emerald-600'
                          : 'text-rose-600'
                      }
                    >
                      {b.budgetStatus === 0 ? 'Compliant' : 'Non-Compliant'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && data.totalCount > data.items.length && (
          <p className="px-6 py-3 text-center text-xs text-slate-400">
            Showing {data.items.length} of {data.totalCount.toLocaleString()} records
          </p>
        )}
      </div>
    </div>
  );
}
